import { useMemo, useState } from 'react';
import { MenuProps, Flex } from 'antd';
import { MoreOutlined } from '@ant-design/icons';

import { MDropdown } from '@Components/Base';
import useUserPermissions from '@Hooks/useUserPermissions';
import { MongoAbility } from '@casl/ability';
import { ReactComponent as CrossIcon } from '@Icons/additional/close-cross.svg';
import { ReactComponent as ViewIcon } from '@Icons/additional/eye.svg';
import { ReactComponent as DeleteIcon } from '@Icons/additional/trash.svg';
import { ReactComponent as TimeLoading } from '@Icons/additional/time-loading.svg';
import { ReactComponent as PlusIcon } from '@Icons/additional/plus.svg';
import { ReactComponent as VendorsIcon } from '@Icons/additional/vendors.svg';
import { ReactComponent as ComparisonIcon } from '@Icons/additional/comparison.svg';
import { ReactComponent as DollarIcon } from '@Icons/additional/dollar.svg';
import { ReactComponent as UpdateIcon } from '@Icons/additional/rotate-360.svg';
import { ReactComponent as DocumentPlusIcon } from '@Icons/additional/document-plus.svg';
import RequiredNotification from '@Components/RequiredNotification/RequiredNotification';

import {
  useGetTenderAccessQuery,
  useCheckHasTenderTaskMutation,
} from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import styles from './TableManageRequestsActions.module.css';
import { getIsCancellableByAccess } from '../../../ComparisonSheet/utils';

interface TableManageRequestsActionsProps {
  status: string;
  access?: number;
  requestNo: number;
  recordKey: string;
  prFor: string;
  onView: () => void;
  onRfq: () => void;
  onVendors: () => void;
  onComparison?: (recordKey: string, taskId: string) => void;
  onPrevious: () => void;
  onUpdate: () => void;
  onExtention: () => void;
  onCancel?: () => void;
  onDelete: () => void;
  onApprovalHistory: () => void;
}

const items = ({
  status,
  prFor,
  access,
  permissions,
  onView,
  onRfq,
  onVendors,
  onComparison,
  onPrevious,
  onUpdate,
  onExtention,
  onCancel,
  onDelete,
  onApprovalHistory,
}: Omit<
  TableManageRequestsActionsProps,
  'requestNo' | 'recordKey' | 'onComparison'
> & {
  onComparison?: () => void;
  permissions: MongoAbility;
}): MenuProps['items'] => {
  const common = {
    style: {
      padding: 0,
    },
  };

  const deletableStatuses = [
    'Awaiting Tender Creation',
    'RFQ Initiation pending',
  ];
  const isTenderCancelled = status === 'Tender Cancelled';
  const nonCancelableStatuses = [
    ...deletableStatuses,
    'Tender Cancelled',
    'CST Approved',
  ];
  const showInitiateTenderAction =
    status === 'RFQ Initiation pending' || status === 'Awaiting Vendor Reply';

  const canBeDeleted = deletableStatuses.includes(status);
  const canBeCanceled =
    !nonCancelableStatuses.includes(status) && getIsCancellableByAccess(access);

  const viewItem = permissions.can('Get', 'TenderRequest')
    ? {
        ...common,
        label: (
          <Flex className="trojan-action-item" onClick={onView}>
            <ViewIcon /> <span>View</span>
          </Flex>
        ),
        key: '0',
      }
    : null;

  const initiateRfqItem = showInitiateTenderAction
    ? {
        ...common,
        label: (
          <Flex className="trojan-action-item" onClick={onRfq}>
            <PlusIcon /> <span>Initiate RFQ</span>
          </Flex>
        ),
        key: '1',
      }
    : null;

  const vendorsListItem = {
    ...common,
    label: (
      <Flex className="trojan-action-item" onClick={onVendors}>
        <VendorsIcon /> <span>Vendors List</span>
      </Flex>
    ),
    key: '2',
  };

  const comparisonSheetItem =
    onComparison && permissions.can('Compare', 'TenderRequest')
      ? {
          ...common,
          label: (
            <Flex className="trojan-action-item" onClick={onComparison}>
              <ComparisonIcon /> <span>Comparison Sheet</span>
            </Flex>
          ),
          key: '3',
        }
      : null;

  const previousPricingItem = {
    ...common,
    label: (
      <Flex className="trojan-action-item" onClick={onPrevious}>
        <DollarIcon /> <span>Previous Pricing</span>
      </Flex>
    ),
    key: '4',
  };

  const updateTenderModeItem = permissions.can('UpdateMode', 'TenderRequest')
    ? {
        ...common,
        label: (
          <Flex className="trojan-action-item" onClick={onUpdate}>
            <UpdateIcon /> <span>Update Tender Mode</span>
          </Flex>
        ),
        key: '5',
      }
    : null;

  const tenderExtentionItem =
    prFor === 'Order' &&
    status === 'RFQ Initiation pending' &&
    permissions.can('Extend', 'TenderRequest')
      ? {
          ...common,
          label: (
            <Flex className="trojan-action-item" onClick={onExtention}>
              <DocumentPlusIcon />
              <span>Tender Extention</span>
            </Flex>
          ),
          key: '6',
        }
      : null;

  const approvalHistoryItem = permissions.can('History', 'TenderRequest')
    ? {
        ...common,
        label: (
          <Flex className="trojan-action-item" onClick={onApprovalHistory}>
            <TimeLoading />
            <span>Approval History</span>
          </Flex>
        ),
        key: '7',
      }
    : null;

  const cancelItem =
    canBeCanceled && permissions.can('Cancel', 'TenderRequest')
      ? {
          ...common,
          label: (
            <Flex className="trojan-action-item" onClick={onCancel}>
              <CrossIcon />
              <span>Cancel</span>
            </Flex>
          ),
          key: '8',
        }
      : null;

  const deleteItem = canBeDeleted
    ? {
        ...common,
        label: (
          <Flex className="trojan-action-item" onClick={onDelete}>
            <DeleteIcon />
            <span>Delete</span>
          </Flex>
        ),
        key: '9',
      }
    : null;

  const output: MenuProps['items'] = isTenderCancelled
    ? [viewItem, approvalHistoryItem]
    : [
        viewItem,
        initiateRfqItem,
        vendorsListItem,
        comparisonSheetItem,
        previousPricingItem,
        updateTenderModeItem,
        tenderExtentionItem,
        approvalHistoryItem,
        cancelItem,
        deleteItem,
      ];

  return output;
};

const TableManageRequestsActions: React.FC<TableManageRequestsActionsProps> = ({
  status,
  requestNo,
  recordKey,
  prFor,
  onView,
  onRfq,
  onVendors,
  onComparison,
  onPrevious,
  onUpdate,
  onExtention,
  onCancel,
  onDelete,
  onApprovalHistory,
}) => {
  const [showDeleteNotification, setShowDeleteNotification] = useState(false);
  const [taskId, setTaskId] = useState<string>();
  const [checkHasTenderTask] = useCheckHasTenderTaskMutation();

  const { data: access } = useGetTenderAccessQuery(recordKey || '', {
    skip: !recordKey,
  });
  const permissions = useUserPermissions();

  const openDeleteNotification = (): void => setShowDeleteNotification(true);
  const closeDeleteNotification = (): void => setShowDeleteNotification(false);

  const performDelete = (): void => {
    closeDeleteNotification();
    onDelete();
  };

  const handleComparisonAction = useMemo(() => {
    if (taskId && onComparison) {
      return () => onComparison(recordKey, taskId);
    }
    return undefined;
  }, [taskId, onComparison, recordKey]);

  return (
    <div>
      <Flex justify="center">
        <MDropdown
          className={styles.dropdown}
          menu={{
            items: items({
              status,
              access,
              prFor,
              permissions,
              onView,
              onRfq,
              onVendors,
              onComparison: handleComparisonAction,
              onPrevious,
              onUpdate,
              onExtention,
              onCancel,
              onDelete: openDeleteNotification,
              onApprovalHistory,
            }),
          }}
          trigger={['click']}
          onOpenChange={async (open) => {
            if (open) {
              await checkHasTenderTask(Number(recordKey))
                .unwrap()
                .then((response) => {
                  if (response && typeof response !== 'object') {
                    setTaskId(response);
                    return;
                  }
                  setTaskId(undefined);
                });
            }
          }}
        >
          <MoreOutlined />
        </MDropdown>
      </Flex>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete tender no.${requestNo}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={closeDeleteNotification}
        handleSuccess={performDelete}
        isDeleteNotification
      />
    </div>
  );
};

export default TableManageRequestsActions;
